/**
 * El período que se está mirando.
 *
 * ---------------------------------------------------------------------------
 *  Por qué es global y no de cada pantalla
 * ---------------------------------------------------------------------------
 *
 * Si cada pantalla tuviera su propio selector, mirar «marzo» en Movimientos y luego
 * ir a Informes mostraría otro mes, y el usuario tendría que volver a elegirlo. Peor
 * aún: podría creer que está viendo el mismo período cuando no lo es, y sacar
 * conclusiones equivocadas de dos pantallas que hablan de meses distintos.
 *
 * Con un solo período compartido, todo lo que se mira habla **siempre** del mismo
 * tramo de tiempo, y el selector de la cabecera sirve desde cualquier pantalla.
 *
 * ---------------------------------------------------------------------------
 *  Por qué se guarda en el navegador
 * ---------------------------------------------------------------------------
 *
 * Al recargar o al volver mañana, se sigue mirando el mismo mes. Perder el período
 * cada vez que se recarga obliga a elegirlo de nuevo decenas de veces al día.
 */

import { create } from 'zustand';

import { todayISO } from '@eldescuadre/core';

/** Los tramos que se pueden elegir. */
export type PeriodKind = 'day' | 'week' | 'month' | 'year' | 'all' | 'custom';

export interface PeriodRange {
  /** Primer día, incluido, en formato `AAAA-MM-DD` local. */
  from: string;
  /** Último día, incluido. */
  to: string;
  /** Cómo se llama el período, para enseñarlo. */
  label: string;
}

const STORAGE_KEY = 'eldescuadre.period';

interface StoredPeriod {
  kind: PeriodKind;
  monthKey: string;
  year: number;
  date: string;
  from: string;
  to: string;
}

/** Suma días a una fecha local, sin pasar por UTC. */
function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(year, month - 1, day + days);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** El lunes de la semana a la que pertenece una fecha. */
function startOfWeek(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(year, month - 1, day);
  // getDay(): 0 es domingo. Se quiere lunes como primer día.
  const weekday = (date.getDay() + 6) % 7;
  return addDays(iso, -weekday);
}

/** El último día de un mes, sin depender de la zona horaria. */
function lastDayOfMonth(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number) as [number, number];
  const day = new Date(year, month, 0).getDate();
  return `${monthKey}-${String(day).padStart(2, '0')}`;
}

/** Los nombres de los meses, para las etiquetas. */
const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

/** «marzo de 2026», que se lee mejor que «2026-03». */
export function monthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number) as [number, number];
  return `${MONTH_NAMES[month - 1]} de ${year}`;
}

/**
 * Convierte el período elegido en un rango de fechas con nombre.
 *
 * Todo se calcula en fecha **local**, nunca en UTC: en Bogotá, un gasto de las 8 de
 * la noche del 31 de marzo pertenece a marzo, no a abril.
 */
export function rangeFor(period: StoredPeriod): PeriodRange {
  switch (period.kind) {
    case 'day':
      return {
        from: period.date,
        to: period.date,
        label: period.date === todayISO() ? 'hoy' : `el ${period.date}`,
      };

    case 'week': {
      const from = startOfWeek(period.date);
      const to = addDays(from, 6);
      return { from, to, label: 'esta semana' };
    }

    case 'year':
      return {
        from: `${period.year}-01-01`,
        to: `${period.year}-12-31`,
        label: `el año ${period.year}`,
      };

    case 'all':
      // Un rango que cubre de sobra cualquier dato real, sin inventarse fechas.
      return { from: '2000-01-01', to: '2100-12-31', label: 'todo el historial' };

    case 'custom':
      return {
        from: period.from,
        to: period.to,
        label: `del ${period.from} al ${period.to}`,
      };

    case 'month':
    default:
      return {
        from: `${period.monthKey}-01`,
        to: lastDayOfMonth(period.monthKey),
        label: monthLabel(period.monthKey),
      };
  }
}

/** El mes al que pertenece una fecha. */
export function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

function defaultStored(): StoredPeriod {
  const today = todayISO();
  return {
    kind: 'month',
    monthKey: monthOf(today),
    year: Number(today.slice(0, 4)),
    date: today,
    from: `${monthOf(today)}-01`,
    to: today,
  };
}

/** Se lee de forma defensiva: no poder leer una preferencia no debe romper la app. */
function readStored(): StoredPeriod {
  const fallback = defaultStored();

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;

    const parsed = JSON.parse(raw) as Partial<StoredPeriod>;
    const kind = parsed.kind;
    const valid: PeriodKind[] = ['day', 'week', 'month', 'year', 'all', 'custom'];
    if (!kind || !valid.includes(kind)) return fallback;

    return {
      kind,
      monthKey: typeof parsed.monthKey === 'string' ? parsed.monthKey : fallback.monthKey,
      year: typeof parsed.year === 'number' ? parsed.year : fallback.year,
      date: typeof parsed.date === 'string' ? parsed.date : fallback.date,
      from: typeof parsed.from === 'string' ? parsed.from : fallback.from,
      to: typeof parsed.to === 'string' ? parsed.to : fallback.to,
    };
  } catch {
    return fallback;
  }
}

function persist(period: StoredPeriod): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(period));
  } catch {
    // No recordarlo no debe impedir usarlo.
  }
}

interface PeriodStore {
  /** Lo que se está mirando. */
  period: StoredPeriod;
  /** El rango ya calculado, que es lo que consumen las pantallas. */
  range: PeriodRange;

  setKind: (kind: PeriodKind) => void;
  setMonth: (monthKey: string) => void;
  setYear: (year: number) => void;
  setDate: (date: string) => void;
  setCustom: (from: string, to: string) => void;
  /** Mueve el período hacia atrás o hacia delante en su propia unidad. */
  shift: (direction: -1 | 1) => void;
}

export const usePeriod = create<PeriodStore>((set, get) => {
  const initial = readStored();

  /** Aplica un cambio y lo deja calculado y guardado, en un solo sitio. */
  const commit = (next: StoredPeriod): void => {
    persist(next);
    set({ period: next, range: rangeFor(next) });
  };

  return {
    period: initial,
    range: rangeFor(initial),

    setKind: (kind) => {
      const current = get().period;
      const today = todayISO();

      commit({
        ...current,
        kind,
        // Al cambiar de tramo, se centra en hoy para no dejar un rango vacío.
        date: kind === 'day' || kind === 'week' ? today : current.date,
        monthKey: kind === 'month' ? monthOf(today) : current.monthKey,
        year: kind === 'year' ? Number(today.slice(0, 4)) : current.year,
        from: kind === 'custom' ? `${monthOf(today)}-01` : current.from,
        to: kind === 'custom' ? today : current.to,
      });
    },

    setMonth: (monthKey) => commit({ ...get().period, kind: 'month', monthKey }),

    setYear: (year) => commit({ ...get().period, kind: 'year', year }),

    setDate: (date) =>
      commit({ ...get().period, kind: 'day', date, monthKey: monthOf(date) }),

    setCustom: (from, to) => {
      // Si el usuario los pone al revés, se ordenan en vez de devolver un rango vacío.
      const [start, end] = from <= to ? [from, to] : [to, from];
      commit({ ...get().period, kind: 'custom', from: start, to: end });
    },

    shift: (direction) => {
      const current = get().period;

      switch (current.kind) {
        case 'day':
          commit({ ...current, date: addDays(current.date, direction) });
          break;

        case 'week':
          commit({ ...current, date: addDays(current.date, direction * 7) });
          break;

        case 'year':
          commit({ ...current, year: current.year + direction });
          break;

        case 'month':
        default: {
          const [year, month] = current.monthKey.split('-').map(Number) as [number, number];
          const date = new Date(year, month - 1 + direction, 1);
          const pad = (n: number): string => String(n).padStart(2, '0');
          commit({
            ...current,
            monthKey: `${date.getFullYear()}-${pad(date.getMonth() + 1)}`,
          });
        }
      }
    },
  };
});
